import readWorkbook from "read-excel-file/browser";
import { unzipSync } from "fflate";

export const CLIENT_ONBOARDING_WORKBOOK_LIMITS = {
  maxFileBytes: 25 * 1024 * 1024,
  maxUncompressedBytes: 200 * 1024 * 1024,
  maxCompressionRatio: 200,
  maxEntries: 10_000,
  maxSheets: 20,
  maxRowsPerSheet: 100_000,
  maxColumnsPerSheet: 256,
  maxHeaderScanRows: 25,
} as const;

export type ClientOnboardingWorkbookFormula = {
  cell: string;
  row: number;
  column: number;
  formula: string;
  cachedValue: string | null;
};

export type ClientOnboardingWorkbookSheet = {
  name: string;
  rows: unknown[][];
  rowCount: number;
  columnCount: number;
  formulas: ClientOnboardingWorkbookFormula[];
};

export type ClientOnboardingWorkbookResult = {
  fileName: string;
  fileSize: number;
  sheets: ClientOnboardingWorkbookSheet[];
  warnings: string[];
  uncompressedBytes: number;
};

export type ClientOnboardingPreparedSheet = {
  headers: string[];
  displayedHeaders: Array<{
    key: string;
    label: string;
    originalLabel: string;
    columnNumber: number;
  }>;
  rows: Array<Record<string, string>>;
  detectedHeaderRow: number;
  totalRows: number;
  importableRows: number;
  skippedBlankRows: number;
  sampleRows: Array<Record<string, string>>;
  warnings: string[];
};

export class ClientOnboardingWorkbookError extends Error {
  readonly code:
    | "unsupported-format"
    | "file-too-large"
    | "malformed-workbook"
    | "encrypted-workbook"
    | "macro-enabled-workbook"
    | "unsafe-workbook"
    | "sheet-limit"
    | "row-limit"
    | "column-limit"
    | "empty-workbook";

  constructor(
    code:
      | "unsupported-format"
      | "file-too-large"
      | "malformed-workbook"
      | "encrypted-workbook"
      | "macro-enabled-workbook"
      | "unsafe-workbook"
      | "sheet-limit"
      | "row-limit"
      | "column-limit"
      | "empty-workbook",
    message: string,
  ) {
    super(message);
    this.name = "ClientOnboardingWorkbookError";
    this.code = code;
  }
}

type ZipEntryMetadata = {
  name: string;
  compressedSize: number;
  uncompressedSize: number;
  encrypted: boolean;
};

const XLSX_MIME_TYPES = new Set([
  "",
  "application/octet-stream",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/zip",
]);

export async function parseClientOnboardingWorkbook(file: File): Promise<ClientOnboardingWorkbookResult> {
  const extension = getFileExtension(file.name);
  if (extension === "xls") {
    throw new ClientOnboardingWorkbookError(
      "unsupported-format",
      "Legacy .xls workbooks are not supported. Save the workbook as .xlsx and upload it again.",
    );
  }
  if (extension === "xlsm" || extension === "xlam") {
    throw new ClientOnboardingWorkbookError(
      "macro-enabled-workbook",
      "Macro-enabled workbooks are not accepted. Save a macro-free .xlsx copy and upload it again.",
    );
  }
  if (extension !== "xlsx") {
    throw new ClientOnboardingWorkbookError("unsupported-format", "Upload a supported .xlsx workbook.");
  }
  if (!XLSX_MIME_TYPES.has(file.type.toLowerCase())) {
    throw new ClientOnboardingWorkbookError(
      "unsupported-format",
      "The selected file does not have a recognized Excel workbook content type.",
    );
  }
  if (file.size > CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxFileBytes) {
    throw new ClientOnboardingWorkbookError(
      "file-too-large",
      `Workbook exceeds the ${formatBytes(CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxFileBytes)} upload limit.`,
    );
  }

  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  if (!hasZipSignature(bytes)) {
    if (hasOleSignature(bytes)) {
      throw new ClientOnboardingWorkbookError(
        "encrypted-workbook",
        "Password-protected or legacy binary workbooks are not supported. Upload an unencrypted .xlsx workbook.",
      );
    }
    throw new ClientOnboardingWorkbookError("malformed-workbook", "The selected file is not a valid .xlsx workbook.");
  }

  const archive = inspectZipArchive(bytes);
  if (archive.entries.some((entry) => entry.encrypted)) {
    throw new ClientOnboardingWorkbookError(
      "encrypted-workbook",
      "Encrypted workbook content is not supported. Remove workbook encryption before upload.",
    );
  }
  if (archive.entries.some((entry) => /(^|\/)vbaProject\.bin$/i.test(entry.name))) {
    throw new ClientOnboardingWorkbookError(
      "macro-enabled-workbook",
      "The workbook contains macros and cannot be imported.",
    );
  }
  if (archive.entries.some((entry) => /^xl\/externalLinks\//i.test(entry.name))) {
    throw new ClientOnboardingWorkbookError(
      "unsafe-workbook",
      "The workbook contains external links. Remove external workbook links before import.",
    );
  }

  let extracted: Record<string, Uint8Array>;
  try {
    extracted = unzipSync(bytes);
  } catch {
    throw new ClientOnboardingWorkbookError(
      "malformed-workbook",
      "The workbook could not be opened. Verify that it is a valid, unencrypted .xlsx file.",
    );
  }

  let parsedSheets: Array<{ sheet: string; data: unknown[][] }>;
  try {
    parsedSheets = await readWorkbook(arrayBuffer, { trim: false }) as Array<{ sheet: string; data: unknown[][] }>;
  } catch {
    throw new ClientOnboardingWorkbookError(
      "malformed-workbook",
      "The workbook content could not be parsed. Verify the file and try again.",
    );
  }

  if (!parsedSheets.length) {
    throw new ClientOnboardingWorkbookError("empty-workbook", "The workbook does not contain any readable sheets.");
  }
  if (parsedSheets.length > CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxSheets) {
    throw new ClientOnboardingWorkbookError(
      "sheet-limit",
      `Workbook contains more than ${CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxSheets} sheets.`,
    );
  }

  const formulaIndex = buildFormulaIndex(extracted);
  const sheets = parsedSheets.map((sheet, index) => {
    const rowCount = sheet.data.length;
    const columnCount = sheet.data.reduce((maximum, row) => Math.max(maximum, row.length), 0);
    if (rowCount > CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxRowsPerSheet) {
      throw new ClientOnboardingWorkbookError(
        "row-limit",
        `${sheet.sheet} exceeds the ${CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxRowsPerSheet.toLocaleString()} row limit.`,
      );
    }
    if (columnCount > CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxColumnsPerSheet) {
      throw new ClientOnboardingWorkbookError(
        "column-limit",
        `${sheet.sheet} exceeds the ${CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxColumnsPerSheet.toLocaleString()} column limit.`,
      );
    }
    return {
      name: sheet.sheet,
      rows: sheet.data,
      rowCount,
      columnCount,
      formulas: formulaIndex[index] || [],
    };
  });

  const formulaCount = sheets.reduce((sum, sheet) => sum + sheet.formulas.length, 0);
  return {
    fileName: file.name,
    fileSize: file.size,
    sheets,
    warnings: formulaCount
      ? [`${formulaCount.toLocaleString()} formula cell${formulaCount === 1 ? "" : "s"} detected. Cached values are used when available; formulas are never executed.`]
      : [],
    uncompressedBytes: archive.uncompressedBytes,
  };
}

export function prepareClientOnboardingSheet(
  sheet: ClientOnboardingWorkbookSheet,
  requestedHeaderRow?: number,
): ClientOnboardingPreparedSheet {
  const detectedHeaderRow = requestedHeaderRow || detectHeaderRow(sheet.rows);
  if (detectedHeaderRow < 1 || detectedHeaderRow > Math.max(1, sheet.rows.length)) {
    throw new ClientOnboardingWorkbookError(
      "malformed-workbook",
      `Header row must be between 1 and ${Math.max(1, sheet.rows.length).toLocaleString()}.`,
    );
  }

  const headerValues = sheet.rows[detectedHeaderRow - 1] || [];
  const displayedHeaders = normalizeHeaders(headerValues);
  const headers = displayedHeaders.map((header) => header.key);
  const rows: Array<Record<string, string>> = [];
  let skippedBlankRows = 0;
  const formulasByCell = new Map(sheet.formulas.map((formula) => [`${formula.row}:${formula.column}`, formula]));

  for (let index = detectedHeaderRow; index < sheet.rows.length; index += 1) {
    const values = sheet.rows[index] || [];
    if (isBlankRow(values)) {
      skippedBlankRows += 1;
      continue;
    }
    rows.push(Object.fromEntries(displayedHeaders.map((header, columnIndex) => [
      header.key,
      formatWorkbookCell(
        values[columnIndex]
        ?? formulasByCell.get(`${index + 1}:${columnIndex + 1}`)?.cachedValue
        ?? null,
      ),
    ])));
  }

  const selectedSheetWarnings = sheet.formulas.map((formula) => {
    const cached = formula.cachedValue === null ? "no cached value" : "cached value retained";
    return `${sheet.name}!${formula.cell}: formula not evaluated (${cached}).`;
  });

  return {
    headers,
    displayedHeaders,
    rows,
    detectedHeaderRow,
    totalRows: sheet.rows.length,
    importableRows: rows.length,
    skippedBlankRows,
    sampleRows: rows.slice(0, 5),
    warnings: selectedSheetWarnings,
  };
}

export function detectHeaderRow(rows: unknown[][]) {
  const scanLimit = Math.min(rows.length, CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxHeaderScanRows);
  let bestRow = 1;
  let bestScore = -1;
  for (let index = 0; index < scanLimit; index += 1) {
    const populated = (rows[index] || []).filter((value) => !isBlankCell(value));
    if (populated.length < 2) continue;
    const textCells = populated.filter((value) => typeof value === "string" && value.trim().length > 0).length;
    const uniqueCells = new Set(populated.map((value) => String(value).trim().toLowerCase())).size;
    const recognizedHeaders = populated.filter((value) =>
      /customer|account|invoice|balance|amount|date|status|collector|business|statement|payment|contact|email|terms|aging/i.test(String(value)),
    ).length;
    const score = populated.length + textCells * 2 + recognizedHeaders * 5 + uniqueCells / Math.max(1, populated.length);
    if (score > bestScore) {
      bestScore = score;
      bestRow = index + 1;
    }
  }
  return bestRow;
}

function inspectZipArchive(bytes: Uint8Array) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const endOfCentralDirectory = findEndOfCentralDirectory(view);
  if (endOfCentralDirectory < 0) {
    throw new ClientOnboardingWorkbookError("malformed-workbook", "Workbook ZIP directory is missing or invalid.");
  }
  const entryCount = view.getUint16(endOfCentralDirectory + 10, true);
  const centralOffset = view.getUint32(endOfCentralDirectory + 16, true);
  if (entryCount === 0xffff || centralOffset === 0xffffffff) {
    throw new ClientOnboardingWorkbookError("unsafe-workbook", "ZIP64 workbooks are not supported for browser import.");
  }
  if (entryCount > CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxEntries) {
    throw new ClientOnboardingWorkbookError("unsafe-workbook", "Workbook archive contains too many internal files.");
  }

  const decoder = new TextDecoder();
  const entries: ZipEntryMetadata[] = [];
  let cursor = centralOffset;
  let uncompressedBytes = 0;
  for (let index = 0; index < entryCount; index += 1) {
    if (cursor + 46 > view.byteLength || view.getUint32(cursor, true) !== 0x02014b50) {
      throw new ClientOnboardingWorkbookError("malformed-workbook", "Workbook ZIP directory is malformed.");
    }
    const flags = view.getUint16(cursor + 8, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const uncompressedSize = view.getUint32(cursor + 24, true);
    const fileNameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const nameStart = cursor + 46;
    const nameEnd = nameStart + fileNameLength;
    if (nameEnd > view.byteLength) {
      throw new ClientOnboardingWorkbookError("malformed-workbook", "Workbook ZIP entry is malformed.");
    }
    const name = decoder.decode(bytes.subarray(nameStart, nameEnd)).replace(/\\/g, "/");
    uncompressedBytes += uncompressedSize;
    if (uncompressedBytes > CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxUncompressedBytes) {
      throw new ClientOnboardingWorkbookError(
        "unsafe-workbook",
        `Workbook expands beyond the ${formatBytes(CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxUncompressedBytes)} safety limit.`,
      );
    }
    const ratio = uncompressedSize / Math.max(1, compressedSize);
    if (uncompressedSize > 1024 * 1024 && ratio > CLIENT_ONBOARDING_WORKBOOK_LIMITS.maxCompressionRatio) {
      throw new ClientOnboardingWorkbookError("unsafe-workbook", "Workbook compression ratio exceeds the safety limit.");
    }
    entries.push({ name, compressedSize, uncompressedSize, encrypted: (flags & 0x1) === 0x1 });
    cursor = nameEnd + extraLength + commentLength;
  }
  return { entries, uncompressedBytes };
}

function buildFormulaIndex(extracted: Record<string, Uint8Array>) {
  const sheetPaths = getOrderedSheetPaths(extracted);
  return sheetPaths.map((path) => {
    const bytes = extracted[path];
    if (!bytes) return [];
    const xml = new TextDecoder().decode(bytes);
    const formulas: ClientOnboardingWorkbookFormula[] = [];
    const cellPattern = /<(?:\w+:)?c\b[^>]*\br="([A-Z]+)(\d+)"[^>]*>([\s\S]*?)<\/(?:\w+:)?c>/gi;
    let cellMatch: RegExpExecArray | null;
    while ((cellMatch = cellPattern.exec(xml))) {
      const body = cellMatch[3];
      const formulaMatch = body.match(/<(?:\w+:)?f\b[^>]*>([\s\S]*?)<\/(?:\w+:)?f>|<(?:\w+:)?f\b[^>]*\/>/i);
      if (!formulaMatch) continue;
      const cachedMatch = body.match(/<(?:\w+:)?v\b[^>]*>([\s\S]*?)<\/(?:\w+:)?v>/i);
      formulas.push({
        cell: `${cellMatch[1]}${cellMatch[2]}`,
        row: Number(cellMatch[2]),
        column: columnLettersToNumber(cellMatch[1]),
        formula: decodeXmlText(formulaMatch[1] || ""),
        cachedValue: cachedMatch ? decodeXmlText(cachedMatch[1]) : null,
      });
    }
    return formulas;
  });
}

function getOrderedSheetPaths(extracted: Record<string, Uint8Array>) {
  const workbookBytes = extracted["xl/workbook.xml"];
  const relationshipBytes = extracted["xl/_rels/workbook.xml.rels"];
  if (!workbookBytes || !relationshipBytes) {
    return Object.keys(extracted)
      .filter((path) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(path))
      .sort((left, right) => left.localeCompare(right, undefined, { numeric: true }));
  }
  const workbookXml = new TextDecoder().decode(workbookBytes);
  const relationshipsXml = new TextDecoder().decode(relationshipBytes);
  const relationships = new Map<string, string>();
  for (const match of relationshipsXml.matchAll(/<(?:\w+:)?Relationship\b([^>]*)\/?>/gi)) {
    const id = match[1].match(/\bId="([^"]+)"/i)?.[1];
    const target = match[1].match(/\bTarget="([^"]+)"/i)?.[1];
    if (id && target) relationships.set(id, normalizeWorkbookPath(target));
  }
  const paths: string[] = [];
  for (const match of workbookXml.matchAll(/<(?:\w+:)?sheet\b[^>]*\br:id="([^"]+)"[^>]*\/?>/gi)) {
    const path = relationships.get(match[1]);
    if (path) paths.push(path);
  }
  return paths;
}

function normalizeHeaders(values: unknown[]) {
  const seen = new Map<string, number>();
  return values.map((value, index) => {
    const originalLabel = formatWorkbookCell(value).trim();
    const baseLabel = originalLabel || `Column ${index + 1}`;
    const normalizedBase = baseLabel.replace(/\s+/g, " ").trim();
    const occurrence = (seen.get(normalizedBase.toLowerCase()) || 0) + 1;
    seen.set(normalizedBase.toLowerCase(), occurrence);
    const key = occurrence === 1 ? normalizedBase : `${normalizedBase} (${occurrence})`;
    return {
      key,
      label: key,
      originalLabel,
      columnNumber: index + 1,
    };
  });
}

function formatWorkbookCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "string") return value.trim();
  return String(value);
}

function findEndOfCentralDirectory(view: DataView) {
  const minimum = Math.max(0, view.byteLength - 65_557);
  for (let offset = view.byteLength - 22; offset >= minimum; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) return offset;
  }
  return -1;
}

function hasZipSignature(bytes: Uint8Array) {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b;
}

function hasOleSignature(bytes: Uint8Array) {
  const signature = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];
  return bytes.length >= signature.length && signature.every((value, index) => bytes[index] === value);
}

function getFileExtension(fileName: string) {
  return fileName.split(".").pop()?.toLowerCase() || "";
}

function isBlankRow(row: unknown[]) {
  return row.every(isBlankCell);
}

function isBlankCell(value: unknown) {
  return value === null || value === undefined || (typeof value === "string" && value.trim().length === 0);
}

function columnLettersToNumber(letters: string) {
  return letters.split("").reduce((value, letter) => value * 26 + letter.charCodeAt(0) - 64, 0);
}

function decodeXmlText(value: string) {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&");
}

function normalizeWorkbookPath(target: string) {
  const normalized = target.replace(/\\/g, "/").replace(/^\/+/, "");
  return normalized.startsWith("xl/") ? normalized : `xl/${normalized.replace(/^\.\//, "")}`;
}

function formatBytes(bytes: number) {
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}
